import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _HelpService;
/*
 *                     NOTICE
 *        COPYRIGHT 2019 - 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { AbWebServices } from "abweb/service/AbWebServices";
import { AB_INITIO_HELP } from "abweb/service/onlineHelp/IHelpService";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { computed, makeObservable, observable } from "mobx";
import { AbHelpFile } from "./AbHelpFile";
import { AbHelpUnversionedPath } from "./AbHelpUnversionedPath";
export const urlToLinkProps = obj => {
  return {
    href: obj.url,
    target: obj.target
  };
};

/**
 * A union of all types of help available, to be used in menus and so on
 */

// re-export for backward compatibility

export let HelpService = (_class = (_HelpService = class HelpService {
  constructor() {
    _initializerDefineProperty(this, "_productHelp", _descriptor, this);
    _initializerDefineProperty(this, "_contextHelp", _descriptor2, this);
    _initializerDefineProperty(this, "_whatsNewHelpModule", _descriptor3, this);
    _initializerDefineProperty(this, "_hideTopicLinks", _descriptor4, this);
    makeObservable(this);
    this._productHelp = undefined;
    this._contextHelp = undefined;
    this._whatsNewHelpModule = undefined;
    this._hideTopicLinks = false;
  }
  get productHelp() {
    return this._productHelp;
  }
  set productHelp(help) {
    if (this._productHelp) {
      HelpService.LOGGER.error(`Product help can only be set once per session. Retaining current product help.\n
      Current product help: ${this._productHelp.label}\n
      New product help: ${help?.label}`);
    } else {
      this._productHelp = help;
    }
  }
  get contextHelp() {
    return this._contextHelp;
  }
  set contextHelp(help) {
    this._contextHelp = help;
  }
  set whatsNewHelpModule(module) {
    if (this._whatsNewHelpModule) {
      HelpService.LOGGER.error(`whatsNewHelpModule can only be set once per session. Retaining current product help.\n
      Current module: ${this._whatsNewHelpModule}\n
      New module: ${module}`);
    } else {
      this._whatsNewHelpModule = module;
    }
  }
  get whatsNewHelpModule() {
    return this._whatsNewHelpModule;
  }
  get hideTopicLinks() {
    return this._hideTopicLinks;
  }

  /**
   * Returns the absolute URL for a specific topic from the Ab Initio online help
   * @param topic   the help topic alias
   * @param [options] {Object} options
   * @param [options.module] the help module. Default: none
   */
  getHelpTopicAbsoluteUrl(topic, options) {
    const modulePiece = options?.module ? `/module/${options.module}` : "";
    const relativeUrl = `/api/abweb/onlineHelp${modulePiece}/topic/${topic}`;
    return AbWebServices.getRPCService().makeAbsoluteGetUrl(relativeUrl);
  }

  /**
   * Displays a specific topic from the Ab Initio online help in a new window/tab
   * @param topic   the help topic alias
   * @param [options] {Object} options
   * @param [options.module] the help module. Default: none
   * @param [options.reuseTab=true] Whether to reuse an existing documentation tab/window if present. Defaults to true
   */
  gotoHelpTopic(topic, options) {
    const absoluteUrl = this.getHelpTopicAbsoluteUrl(topic, options);
    this.navigateTo(absoluteUrl, {
      reuseTab: options?.reuseTab
    });
  }

  /**
  * Navigates to Ab initio Help library.
  */
  gotoHelpLibrary() {
    window.open(this.getHelpUrl(AB_INITIO_HELP).url, "_blank");
  }

  /**
   * @private
   *
   * Performs the necessary operations to bring up the desired help page.
   *
   * @param url The full url to the help page.
   * @param [options] {Object} options
   * @param [options.reuseTab=true] Whether to reuse an existing documentation tab if present. Defaults to true
   */
  navigateTo(url, options) {
    const reuseTab = options?.reuseTab ?? true;
    window.open(url, reuseTab ? HelpService.HELP_WINDOW_NAME : "_blank", "top=25,left=50,height=600,width=800," + "location=yes,status=yes,scrollbars=yes,menubar=yes,toolbar=yes,resizable=yes");
  }
  gotoHelp(help) {
    this.navigateTo(this.getHelpUrl(help).url);
  }
  getHelpUrl(help) {
    if (help === AB_INITIO_HELP) {
      return {
        url: AbWebServices.clientSessionService.abHelpURL || "https://docs.abinitio.com",
        target: "_blank"
      };
    }
    if ("topicAlias" in help) {
      return {
        url: this.getHelpTopicAbsoluteUrl(help.topicAlias, {
          module: help.module
        }),
        target: HelpService.HELP_WINDOW_NAME
      };
    }
    if (ClassUtils.isInstanceOf(help, AbHelpFile)) {
      return {
        url: AbWebServices.getRPCService().makeAbsoluteGetUrl("/api/abweb/onlineHelp/localHelpFile", {
          relativePath: help.relativePath
        }),
        target: HelpService.HELP_WINDOW_NAME
      };
    }
    if (ClassUtils.isInstanceOf(help, AbHelpUnversionedPath)) {
      return {
        url: `${AbWebServices.clientSessionService.abHelpURL}/p/${help.path.replace(/^\//, '')}`,
        target: "_blank"
      }; // strips off any leading slashes from help.path
    }
    if ("type" in help) {
      return {
        url: this.getHelpTopicAbsoluteUrl(`${help.helpModule}_whats_new`, {
          module: help.helpModule
        }),
        target: HelpService.HELP_WINDOW_NAME
      };
    }
    const _exhaustiveCheck = help;
    return _exhaustiveCheck;
  }

  /**
   * Displays the online help Table of Contents page.
   * TODO: Not really implemented yet.
   */
  // public showContents(): void
  // {
  //   const fullUrl: string = "help?tab=contents";
  //   this.navigateTo(fullUrl);
  // }

  /**
   * Displays the online help Search page.
   * TODO: Not really implemented yet.
   */
  // public showSearch(): void
  // {
  //   const fullUrl: string = "help?tab=search";
  //   this.navigateTo(fullUrl);
  // }

  /**
   * Displays the online help Index page.
   * TODO: Not really implemented yet.
   */
  // public showIndex(): void
  // {
  //   const fullUrl: string = "help?tab=index";
  //   this.navigateTo(fullUrl);
  // }

  /**
   * Displays an arbitrary relative url in the browser window or tab used for displaying online help.
   * TODO: Not really implemented yet.
   *
   * @param path A relative path within the help system to display.
   *
   * This method is typically used to display external documentation such as PDF files that are distributed
   * with the online help system.
   */
  // public showInternalNonHelpUrl(path: string): void
  // {
  //   const fullUrl: string = "help?path=" + path;
  //   this.navigateTo(fullUrl);
  // }

  /**
   * Displays an arbitrary url in the browser window or tab used for displaying online help.
   *
   * @param url An absolute url
   */
  // public showUrl(url: string): void
  // {
  //   this.navigateTo(url);
  // }
}, _defineProperty(_HelpService, "HELP_WINDOW_NAME", "AbHelpWnd"), _defineProperty(_HelpService, "LOGGER", LogUtils.getLogger(__filename)), _HelpService), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_productHelp", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "productHelp", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "productHelp"), _class.prototype), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_contextHelp", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "contextHelp", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "contextHelp"), _class.prototype), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_whatsNewHelpModule", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "whatsNewHelpModule", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "whatsNewHelpModule"), _class.prototype), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_hideTopicLinks", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "hideTopicLinks", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hideTopicLinks"), _class.prototype), _class);
//# sourceMappingURL=HelpService.js.map
